import React, { useEffect, useState } from "react";
import { feedbackForEnrichedPhoneEmail, verifyEnrichedEmail } from "../../apis/ext";
import { useMutation } from "react-query";
import TooltipWrapper from "./TooltipWrapper";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuPortal,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import { ChevronDownIcon } from "@radix-ui/react-icons";

import { toast } from "react-hot-toast";
import { useCaptureEvent } from "../../hooks/useCaptureEvent";
import { shortenEmail } from "../../cf";
import Image from "../Tags/Image";

export default function EnrichedEmail({
  value,
  activeWorkspace,
  onSuccess,
  badge = "",
  foundFrom = {},
  feedback = "",
  contactId = "",
  autoVerify = false,
  type = "",
  showDevider = true,
  isVerifying = () => {},
  customEmailVerify = false,
  customEmailVerifyToast = false,
  email = {},
}) {
  const captureEvent = useCaptureEvent();
  const [isCopied, setIsCopied] = useState(false);
  const [showCopy, setShowCopy] = useState(false);
  const copyText = (event, data) => {
    event.stopPropagation();
    event.preventDefault();
    let input = document.createElement("input");
    input.setAttribute("value", data);
    document.body.appendChild(input);
    input.select();
    let result = document.execCommand("copy");
    document.body.removeChild(input);
    setIsCopied(true);
    setTimeout(() => {
      setIsCopied(false);
    }, 1000);

    return result;
  };
  const handleShowCopy = (mouseleave) => {
    if (mouseleave) {
      setShowCopy(false);
    } else {
      setShowCopy(true);
    }
  };

  // verify enriched email
  const verifyEnrichedEmailFn = useMutation("verify_enriched_email", verifyEnrichedEmail, {
    onSuccess(data, variables, context) {
      if (data?.data?.result === "Valid") {
        isVerifying("success");
      }
      onSuccess();
    },
    onError(error) {
      if (error && error.status === 422) {
        isVerifying("error");
      }
    },
  });

  const verifyEmail = (customToast = false) => {
    isVerifying("loading");
    verifyEnrichedEmailFn.mutate({
      data: { email: value },
      options: {
        headers: { "X-Workspace-Id": activeWorkspace?.id },
        showCustomErrorToast: customToast,
      },
    });
  };

  // provide feedback for enriched email

  const feedbackForEnrichedPhoneEmailFn = useMutation(
    "feedback_for_enriched_phone_email",
    feedbackForEnrichedPhoneEmail,
    {
      onSuccess(data, variables, context) {
        toast.success(data.message);
        onSuccess();
      },
      onError(error) {
        console.log(error);
      },
    }
  );

  const provideFeedback = (feedbackValue) => {
    if (feedbackForEnrichedPhoneEmailFn.isLoading) {
      return;
    } else {
      captureEvent("EXP_Person_FeedbackBtn", { feedback: feedbackValue });
      feedbackForEnrichedPhoneEmailFn.mutate({
        data: {
          contact_info_id: email?.id,
          // contact_id: contactId,
          feedback: feedbackValue,
          reason: "",
        },
        options: {
          headers: { "X-Workspace-Id": activeWorkspace?.id },
        },
      });
    }
  };

  const [showDropdown, setShowDropdown] = useState(false);
  const [isDropdownOpened, setIsDropdownOpened] = useState(false);

  useEffect(() => {
    if (isDropdownOpened) {
      setShowDropdown(true);
    } else {
      setShowDropdown(false);
    }
  }, [isDropdownOpened]);

  // auto verify email
  useEffect(() => {
    if (verifyEnrichedEmailFn.isLoading) {
      return;
    }
    if ((autoVerify || customEmailVerify) && !badge) {
      verifyEmail(true);
    }
  }, [customEmailVerify]);

  const returnEmailImg = (type) => {
    if (type === "Work") {
      return "/images/ext/work-email.svg";
    } else if (type === "Personal") {
      return "/images/ext/personal-email.svg";
    } else {
      return "/images/ext/personal-email.svg";
    }
  };

  return (
    <>
      <div className="text-[12px] flex items-center  py-1.5 -ml-5 -mr-4 px-4 hover:bg-gray-100">
        <TooltipWrapper content={`${type} (${foundFrom?.name})`} className="flex">
          <span className="inline-flex w-8 text-center">
            <Image className="no-invert" src={returnEmailImg(type)} alt="Email" width={20} height={20} />
          </span>
        </TooltipWrapper>
        <div
          className={`enriched__phoneemail__hovercard__parent relative flex-1 ${
            showCopy ? "ellipsis_text_showcopy" : ""
          }`}
          onMouseEnter={() => {
            handleShowCopy(false);
            setShowDropdown(true);
          }}
          onMouseLeave={() => {
            if (isDropdownOpened && showDropdown) {
              setIsDropdownOpened(true);
              setShowDropdown(true);
            } else {
              setShowDropdown(false);
              handleShowCopy(true);
            }
          }}
        >
          <span className={`relative flex items-center group  `}>
            <span
              onClick={(e) => {
                copyText(e, value);
                captureEvent("EXP_Person_ClickToCopy", { type: "email" });
              }}
              className={`cursor-pointer text-[14px] text-brand-700 font-semibold custom__tooltip__parent ${
                feedback && feedback !== "Correct" ? "line-through" : ""
              }`}
            >
              {shortenEmail(value)}
              <span className="absolute  -top-8 left-10 w-[90px] custom__tooltip">
                {" "}
                {isCopied ? "Copied" : "Click to copy"}
              </span>
            </span>
            {badge && (
              <TooltipWrapper content={badge}>
                <Image
                  src={`/images/ext/email-status/${badge}.svg`}
                  width={20}
                  height={20}
                  className="ml-2 w-[20px] h-[20px] no-invert"
                />
              </TooltipWrapper>
            )}
          </span>
          {showDropdown && (
            <div
              className="inline-block  bg-background rounded-lg px-3 py-2 animate__animated animate__fadeIn animate__faster enriched__phoneemail__hovercard"
              style={{ boxShadow: "0px 1px 3px var(--gray-400)" }}
            >
              <div className="flex w-full items-center space-x-3 enriched__phoneemail__hovercard__header">
                <span className="inline-flex cursor-pointer" onClick={(e) => copyText(e, value)}>
                  {isCopied ? (
                    <Image className="no-invert" src="/images/ext/check.svg" width={17} height={17} />
                  ) : (
                    <Image className="no-invert" src="/images/ext/copy-paste-2.svg" width={17} height={17} />
                  )}
                </span>

                {verifyEnrichedEmailFn.isLoading ? (
                  <span className="inline-flex cursor-pointer">
                    <div className="loader_circle inline-block"></div>
                  </span>
                ) : (
                  <>
                    {!badge && (
                      <span className="inline-flex cursor-pointer">
                        <TooltipWrapper content="Click to verify" cursor="cursor-pointer">
                          {!verifyEnrichedEmailFn.isLoading && (
                            <Image className="no-invert"
                              onClick={() => {
                                setShowDropdown(false);
                                verifyEmail(customEmailVerifyToast ? true : false);
                                captureEvent("EXP_Person_VerifyBtn", {
                                  type: "email",
                                });
                              }}
                              src="/images/ext/verify-gray-2.svg"
                              width={17}
                              height={17}
                            />
                          )}
                        </TooltipWrapper>
                      </span>
                    )}
                  </>
                )}
                {badge && !verifyEnrichedEmailFn.isLoading && (
                  <span className="inline-flex cursor-pointer">
                    <TooltipWrapper content={badge}>
                      <Image
                        src={`/images/ext/email-status/${badge}.svg`}
                        width={20}
                        height={20}
                        className="w-[20px] h-[20px] no-invert"
                      />
                    </TooltipWrapper>
                  </span>
                )}

                {(showDropdown || feedbackForEnrichedPhoneEmailFn.isLoading) && (
                  <DropdownMenu open={isDropdownOpened} onOpenChange={(open) => setIsDropdownOpened(open)}>
                    <DropdownMenuTrigger className="ml-2 inline-flex focus:outline-none">
                      {!feedbackForEnrichedPhoneEmailFn.isLoading && (
                        <Image className="no-invert" src="/images/ext/thumb-down.svg" width={17} height={17} />
                      )}
                    </DropdownMenuTrigger>
                    <DropdownMenuPortal>
                      <DropdownMenuContent className="w-full mt-1.5 ml-3" align="end">
                        <DropdownMenuGroup>
                          <DropdownMenuItem onClick={() => provideFeedback("Correct")}>
                            <Image src="/images/ext/thumb-up.svg" className="mr-2 no-invert" width={17} height={17} />
                            Correct
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => provideFeedback("Wrong")}>
                            <Image
                              src="/images/ext/cross-black.svg"
                              className="mr-2 no-invert"
                              width={17}
                              height={17}
                            />
                            Wrong
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => provideFeedback("Bounced")}>
                            <Image
                              src="/images/ext/info-question.svg"
                              className="mr-2 no-invert"
                              width={17}
                              height={17}
                            />
                            Bounced
                          </DropdownMenuItem>
                        </DropdownMenuGroup>
                      </DropdownMenuContent>
                    </DropdownMenuPortal>
                  </DropdownMenu>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
      {/* {showDevider && (
        <div class="w-[240px] h-[1px] bg-border mx-auto my-2"></div>
      )} */}
    </>
  );
}
